// Vocabulario unico de campos de formulario del backoffice.
//
// Antes cada pagina escribia sus clases a mano y habia SEIS definiciones
// distintas de `inputCls` (Account, Coupons, Admins, Invoices, PartnerEdit,
// DynamicForm) mas decenas de strings inline: 63 variantes de clases para tres
// tipos de control. El resultado era drift en cinco ejes a la vez — cuatro
// radios (`rounded` / `rounded-lg` / `rounded-md` / ninguno), cuatro fondos
// (`bg-surface-muted` / `bg-transparent` / `bg-surface` / ninguno), tres altos
// y tres tamaños de texto.
//
// Y no era solo cosmetico. De esas variantes, 26 controles iban con
// `bg-transparent` y 14 de esos ademas forzaban `text-ink dark:text-ink-inverted`.
// En un <select> ese par es el bug que se veia en el tema oscuro: el popup no
// hereda el fondo transparente del control — cae al lienzo del navegador, que es
// blanco — pero el texto del <option> SI hereda el color claro. Blanco sobre
// blanco.
//
// Reglas que sostiene este modulo, y por las que un call-site no deberia
// escribir clases de campo a mano:
//
//   1. El fondo es SIEMPRE opaco. `bg-transparent` en un campo delega el fondo a
//      lo que haya detras, y para un <select> "lo que hay detras" del popup no
//      es la tarjeta sino el chrome del navegador.
//   2. El color de texto es SIEMPRE explicito, nunca heredado. Fondo y texto
//      tienen que venir del mismo lugar o pueden divergir.
//   3. El focus ring es parte del campo, no un extra opcional. Antes lo tenian
//      5 de 117 controles.
//
// Los defaults (radio `rounded`, fondo `surface-muted`, alto 2.5rem) son los que
// ya eran mayoritarios en el portal: unificar no debia repintar el backoffice.

/** Alto y densidad del campo. `md` es el default del portal. */
export type FieldSize = 'sm' | 'md' | 'lg';

export interface FieldOptions {
  /**
   * `sm` para campos dentro de una fila densa (editores de JSON, tablas),
   * `md` para filtros y formularios normales, `lg` para el cuerpo principal de
   * un alta donde el campo es el protagonista.
   */
  size?: FieldSize;
  /** Fuente monoespaciada: IDs, claves, JSON, valores de config. */
  mono?: boolean;
  /** Marca el borde en rojo. Para errores de validacion. */
  invalid?: boolean;
  /** `w-full`. Default `true`; ponelo en `false` para campos que van inline. */
  block?: boolean;
  /** Clases extra del call-site (ancho fijo, margenes, `flex-1`). */
  extra?: string;
}

/*
 * Fondo y texto salen juntos y siempre opacos — ver regla 1 y 2 arriba.
 * `surface-muted` hunde levemente el campo respecto de la tarjeta que lo
 * contiene, que es como se lee un control editable en el resto del portal.
 */
const SURFACE =
  'bg-surface-muted text-ink dark:bg-surface-dark-muted dark:text-ink-inverted';

const BORDER = 'border border-surface-border dark:border-surface-dark-border';

/*
 * `focus:outline-none` saca el outline del UA (que en algunos motores queda
 * pegado al borde y se lee como un borde doble) y lo reemplaza por el ring de
 * marca. `focus-visible` y no `focus`: el ring aparece con teclado, no al
 * clickear.
 */
const FOCUS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-light';

const DISABLED = 'disabled:opacity-50 disabled:cursor-not-allowed';

const SIZES: Record<FieldSize, string> = {
  sm: 'min-h-[2.25rem] px-2 py-1 text-xs',
  md: 'min-h-[2.5rem] px-2 py-1 text-sm',
  lg: 'min-h-[2.75rem] px-3 py-2 text-sm',
};

/**
 * Clases de un campo nativo — `<input>`, `<select>` o `<textarea>`.
 *
 * Los tres comparten vocabulario a proposito: en una misma columna de
 * formulario un text input y un select tienen que cerrar al mismo alto, con el
 * mismo radio y el mismo borde, o la fila se ve desalineada.
 *
 * El popup del `<select>` ademas depende de dos cosas que NO viven aca porque
 * no son clases de utilidad: la propiedad `color-scheme` atada a `.dark` y la
 * regla base de `option`/`optgroup`. Las dos estan en `src/index.css`.
 */
export function fieldCls(opts: FieldOptions = {}): string {
  const { size = 'md', mono = false, invalid = false, block = true, extra = '' } = opts;

  return [
    'rounded',
    block ? 'w-full' : '',
    SIZES[size],
    SURFACE,
    invalid ? 'border border-status-error' : BORDER,
    mono ? 'font-mono' : '',
    FOCUS,
    DISABLED,
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Clases de un checkbox o radio nativo.
 *
 * No comparte nada con `fieldCls()` — no tiene alto de fila, ni padding, ni fondo
 * propio. Lo unico que necesita es tamaño, el acento de marca (para que el
 * check pintado por el navegador use el violeta del portal y no el azul del
 * sistema) y el mismo focus ring que el resto.
 */
export function checkboxCls(extra = ''): string {
  return `h-4 w-4 accent-brand ${FOCUS}${extra ? ` ${extra}` : ''}`;
}
